import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Youtube } from "lucide-react";
import { useI18n } from "../contexts/I18nContext";
import { useSiteSettings } from "../contexts/SiteSettingsContext";
import XIcon from "./XIcon";

const LOCATION_MAP_URL = "https://maps.google.com/maps?svid=CAwSHRIbCgNwdnESFENnMHZaeTh4TVhBd2VubDBOM0Z1GAo&um=1&ie=UTF-8&fb=1&gl=sa&sa=X&ftid=0x3e2ee17fdd39e7db:0xe1fa78d8f15c6622";

function WhatsAppIcon({ size = 20, className = "", ...props }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" className={className} {...props}>
            <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2.05 22l5.27-1.38a9.9 9.9 0 0 0 4.72 1.2h.01c5.46 0 9.91-4.45 9.91-9.91S17.5 2 12.04 2Zm0 18.15h-.01a8.22 8.22 0 0 1-4.18-1.14l-.3-.18-3.13.82.84-3.05-.2-.31a8.2 8.2 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.25-8.24a8.22 8.22 0 0 1 8.24 8.24c0 4.55-3.7 8.24-8.24 8.24Zm4.52-6.17c-.25-.12-1.47-.73-1.7-.81-.23-.09-.39-.12-.56.12-.17.25-.64.81-.79.98-.15.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.24-.74-.66-1.24-1.47-1.38-1.72-.15-.25-.02-.38.11-.51.11-.11.25-.29.37-.44.12-.15.17-.25.25-.42.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.49-.41-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.91 2.44 1.03 2.61.12.17 1.78 2.72 4.31 3.82.6.26 1.07.41 1.44.53.61.19 1.16.17 1.59.1.49-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.22-.16-.47-.29Z" />
        </svg>
    );
}

export default function Footer() {
    const { t, lang } = useI18n();
    const { settings } = useSiteSettings();
    const year = new Date().getFullYear();

    const email = settings.contact_email || "info@Legends.sa";
    const phone = settings.contact_phone || "";
    const address = (lang === "ar" ? settings.contact_address_ar : settings.contact_address_en) ||
        (lang === "ar" ? "الرياض، المملكة العربية السعودية" : "Riyadh, Saudi Arabia");

    const primaryLinks = [
        { to: "/", k: "nav_home" },
        { to: "/about", k: "nav_about" },
        { to: "/services", k: "nav_services" },
        { to: "/work", k: "nav_works" },
        { to: "/clients", k: "nav_clients" },
        { to: "/contact", k: "nav_contact" },
    ];
    const resourceLinks = [
        { to: "/welcome", k: "nav_welcome" },
        { to: "/news", k: "nav_news" },
        { to: "/global-reach", k: "nav_global" },
    ];
    const socials = [
        { url: settings.social_linkedin, Icon: Linkedin, label: "LinkedIn", testid: "social-linkedin" },
        { url: settings.social_instagram, Icon: Instagram, label: "Instagram", testid: "social-instagram" },
        { url: settings.social_x, Icon: XIcon, label: "X", testid: "social-x" },
        { url: settings.social_youtube, Icon: Youtube, label: "YouTube", testid: "social-youtube" },
        {
            url: settings.social_whatsapp
                ? (settings.social_whatsapp.startsWith("http")
                    ? settings.social_whatsapp
                    : `https://wa.me/${settings.social_whatsapp.replace(/[^0-9]/g, "")}`)
                : "",
            Icon: WhatsAppIcon,
            label: "WhatsApp",
            testid: "social-whatsapp",
        },
    ].filter((s) => s.url);

    return (
        <footer className="site-footer" data-testid="site-footer">
            <div className="site-footer-inner">
                <div className="footer-main-grid">

                    <nav className="footer-link-column" aria-label="Footer sitemap">
                        <div className="footer-column-title">{lang === "ar" ? "خريطة الموقع" : "Sitemap"}</div>
                        <ul data-testid="footer-sitemap-primary">
                            {primaryLinks.map((n) => (
                                <li key={n.to}>
                                    <Link to={n.to} className="footer-text-link">{t(n.k)}</Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav className="footer-link-column" aria-label="Footer company links">
                        <div className="footer-column-title">{lang === "ar" ? "الشركة" : "Company"}</div>
                        <ul data-testid="footer-sitemap-brand">
                            {resourceLinks.map((n) => (
                                <li key={n.to}>
                                    <Link to={n.to} className="footer-text-link">{t(n.k)}</Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className="footer-contact-card">
                        <div className="footer-column-title">{lang === "ar" ? "تواصل معنا" : "Reach us"}</div>
                        <ul>
                            <li className="footer-contact-row">
                                <a href={`mailto:${email}`} data-testid="footer-email">{email}</a>
                            </li>
                            {phone && (
                                <li className="footer-contact-row">
                                    <a href={`tel:${phone}`} data-testid="footer-phone">{phone}</a>
                                </li>
                            )}
                            <li className="footer-contact-row">
                                <a href={LOCATION_MAP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-address">{address}</a>
                            </li>
                        </ul>
                        {socials.length > 0 && (
                            <div className="footer-socials">
                                {socials.map(({ url, Icon, label, testid }) => (
                                    <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label} className="footer-social-link" data-testid={testid}>
                                        <Icon size={19} />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>

                </div>

                <div className="footer-bottom-bar">
                    <div>© {year} Legends. {t("rights")}.</div>
                    <div className="footer-bottom-links">
                        {[...primaryLinks.slice(0, 2), { to: "/contact", k: "nav_contact" }].map((n) => (
                            <Link key={n.to} to={n.to}>{t(n.k)}</Link>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
}
